import { Col, Form, Row, Spinner } from 'react-bootstrap'
import React, { useEffect } from 'react'
import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectCreateNewCRMContact,
  selectExternalId,
  selectIsInitializing,
  selectIsLoadingContact,
  selectShouldCreateContact,
  selectShowLinkContact,
  setContact,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
  setIsInitializing,
  setNewContactData,
  setShouldCreateContact,
  setShouldLinkContact,
} from '../../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { Button } from '../../../components/Button/Button'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { User } from '../../../models/User'
import { useFetchContact } from '../hooks'
import { UpdateExternalContactCreateSection } from './UpdateExternalContactCreateSection'
import { UpdateExternalContactLinkSection } from './UpdateExternalContactLinkSection'

type ContactSelectStepProps = {
  serviceId: IntegrationServiceId
  onCancel: () => void
  user: User
}

type WealthContactCreateMetadata = {
  sendInvite: boolean
}

type ContactCreateMetadata = WealthContactCreateMetadata

type ContactSelectFormValue = {
  createNewContact: boolean
  firstName?: string
  lastName?: string
  email?: string
  name?: string
  selectedExternalId?: string
  createNewZocksContact: boolean
  linkToExistingZocksContact: boolean
  metadata?: ContactCreateMetadata
}

export const UpdateExternalContactSelectStep: React.FC<ContactSelectStepProps> = ({ serviceId, onCancel, user }) => {
  const dispatch = useAppDispatch()
  const isLoadingContact = useAppSelector(selectIsLoadingContact)

  const createNewCRMContact = useAppSelector(selectCreateNewCRMContact)

  const shouldCreateContact = useAppSelector(selectShouldCreateContact)
  const shouldLinkContact = useAppSelector(selectShowLinkContact)
  const isInitializing = useAppSelector(selectIsInitializing)
  const externalId = useAppSelector(selectExternalId)
  const contact = useAppSelector(selectContact)

  const form = useForm<ContactSelectFormValue>()
  const createNewContact = useWatch({ control: form.control, name: 'createNewContact' })

  const handleContinue = (formData: ContactSelectFormValue) => {
    dispatch(setCreateNewCRMContact(formData.createNewContact))
    dispatch(setShouldLinkContact(formData.linkToExistingZocksContact))
    const createNewZocksContact = formData.createNewContact ? formData.createNewZocksContact : shouldCreateContact
    dispatch(setShouldCreateContact(createNewZocksContact))
    if (formData.createNewContact) {
      dispatch(
        setNewContactData({
          firstName: formData.firstName,
          lastName: formData.lastName,
          name: formData.name,
          email: formData.email,
          metadata: formData.metadata,
        }),
      )
    }
    if (!formData.createNewContact && formData.email && contact) {
      dispatch(setContact({ ...contact, email: formData.email }))
    }
    dispatch(setIsInitializing(false))
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
  }
  const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)

  const { searchForEmail } = useFetchContact({ serviceId })

  useEffect(() => {
    form.reset({
      createNewContact: createNewCRMContact,
      createNewZocksContact: shouldCreateContact,
      linkToExistingZocksContact: shouldLinkContact,
    })
  }, [createNewCRMContact, shouldCreateContact, shouldLinkContact, form.reset, form])

  useEffect(() => {
    if (createNewContact) {
      dispatch(setContact(null))
      dispatch(setExternalId(null))
      return
    }
    if (!externalId && user.email) {
      searchForEmail(user.email)
    } else {
      dispatch(setIsInitializing(false))
    }
  }, [createNewContact, dispatch, user.email, searchForEmail, externalId])

  if (isInitializing) {
    return (
      <div className='d-flex align-items-center justify-content-center mb-3'>
        <Spinner />
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      <div className='vh-100 ms-3 me-3 d-flex flex-column'>
        <div className='flex-grow-1 overflow-hidden'>
          <Row className='mb-4'>
            <Col>Choose how to save this contact to {integrationDisplayName}</Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='createNewContact'
                control={form.control}
                render={({ field }) => (
                  <Form.Check
                    inline
                    type='radio'
                    name='createNewContact-group'
                    className='pb-2'
                    label={`Create new contact in ${integrationDisplayName}`}
                    id='create-new-contact'
                    checked={field.value === true}
                    value={'create-new-contact'}
                    disabled={isLoadingContact}
                    onChange={() => {
                      field.onChange(true)
                    }}
                  />
                )}
              />
            </Col>
          </Row>
          {createNewContact ? <UpdateExternalContactCreateSection serviceId={serviceId} /> : null}
          <Row className='mt-3'>
            <Col>
              <Controller
                name='createNewContact'
                control={form.control}
                render={({ field }) => (
                  <Form.Check
                    inline
                    type='radio'
                    className='pb-2'
                    name='createNewContact-group'
                    label={` Save to existing contact in ${integrationDisplayName}`}
                    id='link-to-contact'
                    checked={field.value === false}
                    value={'link-to-contact'}
                    disabled={isLoadingContact}
                    onChange={() => {
                      field.onChange(false)
                    }}
                  />
                )}
              />
            </Col>
            {!createNewContact ? <UpdateExternalContactLinkSection serviceId={serviceId} /> : null}
          </Row>
        </div>

        <div className='flex-shrink-0 pt-3'>
          <Row className='justify-content-end mb-4'>
            <Col xs='auto'>
              <Button className='mt-2 me-auto' variant='secondary' onClick={onCancel}>
                Cancel
              </Button>
            </Col>
            <Col xs='auto'>
              <Button
                className='mt-2 me-auto'
                variant='primary'
                onClick={form.handleSubmit(handleContinue)}
                disabled={isLoadingContact || (!contact && !createNewContact)}
              >
                Continue
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </FormProvider>
  )
}
